import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { Exercise } from "@modules/sessionBlock/exercises/exerciseTypes"
import { useCommentFormik } from "@modules/sessionBlock/exercises/hooks/useCommentFormik"
import { ParentSessionOrigin } from "@modules/sessionBlock/exercises/sessionTypes"
import { MediaUpload } from "@modules/ui/components/fileUpload/mediaUpload"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import get from "lodash/get"
import { InputSwitch } from "primereact/inputswitch"

type CommentFormProps = {
  exercise: Exercise
  parentSessionOrigin: ParentSessionOrigin
  hideCommentForm: () => void
} & React.HTMLAttributes<HTMLDivElement>

export const CommentForm: React.FC<CommentFormProps> = ({ exercise, parentSessionOrigin, hideCommentForm }) => {
  const { commentFormik, isSubmitting } = useCommentFormik(parentSessionOrigin, exercise, hideCommentForm)
  const isUploadingAMedia = commentFormik.values.medias.some((media) => media.isUploading)

  return (
    <div className="flex flex-column pb-3 gap-3">
      {/* Comment form title*/}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TL">{word("session.comment.title")}</HUText>
        <HUText fontStyle="BS">{word("session.comment.subtitle")}</HUText>
      </div>

      {/* Comment input */}
      <div className={`mt-3 p-3 rounded-big-radius bg-neutralColor-100`}>
        <FormikInput
          flex
          isRequiredInput
          label={word("session.scoreboard.card.comment")}
          placeHolder={word("exercise.score.addComment")}
          formikStyle={{ display: "flex", flexGrow: 1 }}
          getFieldProps={commentFormik.getFieldProps}
          setFieldValue={commentFormik.setFieldValue}
          isInvalid={isFormFieldInValid("note", commentFormik)}
          name="note"
          type="textarea"
          textareaProps={{ autoResize: true, rows: 5, maxLength: 100 }}
        />
      </div>

      {/* Video upload */}
      <MediaUpload
        name="medias"
        className="my-2"
        mediaList={commentFormik.values.medias}
        updateMediaList={(mediaList) => {
          commentFormik.setFieldValue("medias", mediaList)
        }}
        updateMediaItem={(mediaItem, index) => {
          commentFormik.setFieldValue(`medias[${index}]`, mediaItem)
        }}
      />

      {typeof get(commentFormik.errors, "medias") === "string" && getFormErrorMessage("medias", commentFormik)}

      {/* Comment visibility switch */}
      <div className="px-2 gap-4">
        <HUText className="my-1 uppercase" fontStyle="LS">
          {word("session.comment.visibility.label")}
        </HUText>
        <div className="mt-2 flex flex-row align-items-center justify-content-between w-full gap-2">
          <HUText fontStyle="BS" className="flex-1">
            {word("session.comment.visibility.description")}
          </HUText>
          <InputSwitch
            checked={commentFormik.values.noteAndMediaIsVisible}
            onChange={() =>
              commentFormik.setFieldValue("noteAndMediaIsVisible", !commentFormik.values.noteAndMediaIsVisible)
            }
          />
        </div>
      </div>

      {/* Save button */}
      <HUButton
        type="Default"
        size="M"
        colorType="Primary"
        text={word("global.save")}
        onClick={() => commentFormik.submitForm()}
        className="w-full mt-3"
        loading={isSubmitting}
        disabled={isUploadingAMedia || isSubmitting}
      />
    </div>
  )
}
